import { OmDialog } from '@owma/kit'
import i18n from 'i18n'
import React, { useEffect, useState } from 'react'

import styles from './style.module.scss'

import FolderListItem from 'pages/calendar/components/FolderListItem'

import FolderEditor from 'pages/calendar/components/FolderEditor'
import FolderShareInfo from 'pages/calendar/components/FolderShareInfo'
import * as todoFolderManager from 'pages/todo/modules/TodoFolderManager'
import { getSortedFolders } from 'pages/todo/modules/TodoFolderSync'
import * as todoSync from 'pages/todo/modules/TodoSync'
import { confirmDelete } from 'utils/ui'

interface Folder {
  url: string
  displayName: string
  isSync: boolean
  calendarColor: {
    _cdata: string
  }
  isShared: boolean
  isSharedOwner: boolean
  isDefault: boolean
  folderType: string
  owner?: string
  userInfo?: {
    email: string
  }
  color?: string
  isReadOnly?: boolean
}

interface EditInfo {
  displayName: string
  color: string
}

interface TaskFolderListProps {
  showInCalendar?: boolean
}

const TaskFolderList: React.FC<TaskFolderListProps> = ({ showInCalendar = true }) => {
  const [folders, setFolders] = useState<Folder[]>(getSortedFolders() as Folder[])
  const [showFolderEditor, setShowFolderEditor] = useState(false)
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null)

  // Share modal states
  const [showShareInfoModal, setShowShareInfoModal] = useState(false)
  const [shareInfoFolder, setShareInfoFolder] = useState<Folder | null>(null)

  const [hasSharedFolders, setHasSharedFolders] = useState(false)

  const folderListener = () => {
    const folders = getSortedFolders() as Folder[]
    setHasSharedFolders(folders.some((folder: Folder) => folder.isShared))
    setFolders(folders)
  }

  useEffect(() => {
    folderListener()

    todoSync.addTodoFolderListener(folderListener)
    return () => {
      todoSync.removeTodoFolderListener(folderListener)
    }
  }, [])

  const onCheckClick = (isChecked: boolean, folderId: string) => {
    todoSync.enableSync(folderId, isChecked)
  }

  const onFolderEdit = (folderId: string) => {
    setShowFolderEditor(true)
    const targetFolder = folders.find((f) => f.url === folderId)
    if (targetFolder) {
      const folderWithColor = { ...targetFolder, color: targetFolder.calendarColor._cdata }
      setEditingFolder(folderWithColor)
    }
  }

  function onShowFolderShareDialog(url: string) {
    const folder = folders.find((f) => f.url === url)
    if (!folder) return

    setShowShareInfoModal(true)
    setShareInfoFolder(folder)
  }

  const handleShareInfoClose = () => {
    // request todo folder udpate.
    setShowShareInfoModal(false)
    setShareInfoFolder(null)
  }

  const handleFolderEditorClose = () => {
    setShowFolderEditor(false)
    setEditingFolder(null)
  }

  const handleFolderSave = (editInfo: EditInfo) => {
    if (editingFolder) {
      todoFolderManager.updateFolder(editingFolder.url, editInfo.displayName, editInfo.color)
      handleFolderEditorClose()
    }
  }

  const onDeleteClick = (folderId: string) => {
    handleFolderRemove(folderId)
    //todoFolderManager.deleteTodoFolder(folderId)
  }

  /**
   *  처리 흐름:
   *    - handleFolderRemove → deleteTodoFolder
   *    - Server DELETE 요청 → 성공 시:
   *      - Cache invalidation (전체 캐시 무효화)
   *      - fetchTodoFolders(true, ...) (강제 폴더 목록 갱신)
   *    - DB 처리: removeTodoItems에서 이미 제대로 처리됨 (DB 삭제 + cache invalidation)
   */
  const handleFolderRemove = (folderId: string) => {
    confirmDelete({
      title: i18n.t('todo.editor.deleteTodoFolder'),
      content: i18n.t('todo.editor.deleteTodoFolderConfirm').replace(/<br\/>/g, '\n'),
      okText: i18n.t('common.button.ok'),
      cancelText: i18n.t('common.button.cancel'),
      onOk: () => {
        // 폴더 삭제 기능
        todoFolderManager.deleteTodoFolder(folderId, (success: boolean) => {
          if (success) {
            console.log('Todo folder deleted successfully')
          } else {
            console.error('Failed to delete todo folder')
          }
          handleFolderEditorClose()
        })
      },
      onCancel: () => {
        console.log('Cancel')
      },
    })
  }

  const renderFolderListItem = (folder: Folder) => {
    return (
      <FolderListItem
        key={folder.url}
        folderId={folder.url}
        displayName={folder.displayName}
        isChecked={folder.isSync}
        color={folder.calendarColor._cdata}
        isShared={folder.isShared}
        isSharedOwner={folder.isSharedOwner}
        isDefault={folder.isDefault}
        isCalendarFolder={folder.folderType === 'calendar'}
        isTodoFolder={true}
        onCheckClick={onCheckClick}
        onFolderEdit={onFolderEdit}
        onShareClick={onShowFolderShareDialog}
        onDeleteClick={onDeleteClick}
      />
    )
  }

  return folders.length > 0 ? (
    <React.Fragment>
      <div className={styles.taskFolderList}>
        {(showInCalendar || hasSharedFolders) && (
          <div className={styles.todoFolderListTitle}>{i18n.t('calendar.folder.todoFolders')}</div>
        )}
        {folders.filter((folder) => !folder.isShared).map((folder) => renderFolderListItem(folder))}
        {hasSharedFolders && (
          <>
            <div className={styles.todoFolderListTitle}>
              {i18n.t('calendar.folder.sharedFolders')}
            </div>

            {folders
              .filter((folder) => folder.isShared)
              .map((folder) => renderFolderListItem(folder))}
          </>
        )}
      </div>

      <OmDialog
        open={showFolderEditor}
        onClose={handleFolderEditorClose}
        width={480}
        destroyOnHidden
        closable={false}
        contentStyle={{ padding: 0, overflow: 'hidden', maxHeight: '90vh' }}
      >
        <FolderEditor
          folder={editingFolder}
          onSave={handleFolderSave}
          onClose={handleFolderEditorClose}
          isCalendarFolder={false}
        />
      </OmDialog>

      <OmDialog
        open={showShareInfoModal}
        onClose={handleShareInfoClose}
        width={480}
        destroyOnHidden
        closable={false}
        contentStyle={{ padding: 0, overflow: 'hidden', maxHeight: '90vh' }}
      >
        <FolderShareInfo onClose={handleShareInfoClose} folder={shareInfoFolder as any} />
      </OmDialog>
    </React.Fragment>
  ) : null
}

export default TaskFolderList
